import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPen, faCircleXmark, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { TagModel } from '../../models/CRM';
import { useToast } from '@shared/hooks/use-toast';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag } from '../../hooks/useTags';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';
import { ColorPicker } from '../interaction/ColorPicker';
import { getTextColorForBackground } from '../../utils/colorUtils';

export interface ManageTagsModalRef {
  open: () => void;
}

interface ManageTagsModalProps {
  onSuccess?: () => void;
}

export const ManageTagsModal = forwardRef<ManageTagsModalRef, ManageTagsModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const deleteConfirmRef = useRef<ConfirmModalRef>(null);

  const { tags, isLoading, refetch } = useTags({ per_page: 100 });
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();
  const { canManage } = useCanManageCRM();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#000000');
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('#000000');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({
    open: () => {
      setNewName('');
      setNewColor('#000000');
      setEditingId(null);
      setDeletingId(null);
      modalRef.current?.openModal();
      refetch();
    },
  }));

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) {
      toast({
        title: t('tagsManagement.createError.title', 'Error al crear etiqueta'),
        description: t('tagsManagement.createError.nameRequired', 'El nombre es requerido'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await createTagMutation.mutateAsync({
        name: newName.trim(),
        color: newColor,
      });
      toast({
        title: t('tagsManagement.createSuccess.title', 'Etiqueta creada'),
        description: t('tagsManagement.createSuccess.description', 'La etiqueta se ha creado correctamente.'),
        variant: 'default',
      });
      setNewName('');
      setNewColor('#000000');
      refetch();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: t('tagsManagement.createError.title', 'Error al crear etiqueta'),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t('tagsManagement.createError.description', 'Error inesperado.'),
        variant: 'destructive',
      });
    }
  }, [newName, newColor, createTagMutation, t, toast, refetch, onSuccess]);

  const handleStartEdit = useCallback((tag: TagModel) => {
    setEditingId(tag.id);
    setEditingName(tag.name);
    setEditingColor(tag.color || '#000000');
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingName('');
    setEditingColor('#000000');
  }, []);

  const handleSaveEdit = useCallback(
    async (tagId: number) => {
      if (!editingName.trim()) {
        toast({
          title: t('tagsManagement.updateError.title', 'Error al actualizar etiqueta'),
          description: t('tagsManagement.updateError.nameRequired', 'El nombre es requerido'),
          variant: 'destructive',
        });
        return;
      }

      try {
        await updateTagMutation.mutateAsync({
          tagId,
          data: {
            name: editingName.trim(),
            color: editingColor,
          },
        });
        toast({
          title: t('tagsManagement.updateSuccess.title', 'Etiqueta actualizada'),
          description: t('tagsManagement.updateSuccess.description', 'La etiqueta se ha actualizado correctamente.'),
          variant: 'default',
        });
        setEditingId(null);
        refetch();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: t('tagsManagement.updateError.title', 'Error al actualizar etiqueta'),
          description:
            error?.response?.data?.message ||
            error?.message ||
            t('tagsManagement.updateError.description', 'Error inesperado.'),
          variant: 'destructive',
        });
      }
    },
    [editingName, editingColor, updateTagMutation, t, toast, refetch, onSuccess],
  );

  const handleDeleteClick = useCallback(
    (tagId: number) => {
      setDeletingId(tagId);
      const tag = tags.find((t) => t.id === tagId);
      deleteConfirmRef.current?.open(
        async () => {
          try {
            await deleteTagMutation.mutateAsync(tagId);
            toast({
              title: t('tagsManagement.deleteSuccess.title', 'Etiqueta eliminada'),
              description: t('tagsManagement.deleteSuccess.description', 'La etiqueta se ha eliminado correctamente.'),
              variant: 'default',
            });
            setDeletingId(null);
            refetch();
            onSuccess?.();
          } catch (error: any) {
            toast({
              title: t('tagsManagement.deleteError.title', 'Error al eliminar etiqueta'),
              description:
                error?.response?.data?.message ||
                error?.message ||
                t('tagsManagement.deleteError.description', 'Error inesperado.'),
              variant: 'destructive',
            });
            setDeletingId(null);
          }
        },
        undefined,
        t('tagsManagement.deleteConfirm.title', 'Eliminar etiqueta'),
        t('tagsManagement.deleteConfirm.message', '¿Estás seguro de que deseas eliminar esta etiqueta?').replace(
          '{{name}}',
          tag?.name || '',
        ),
      );
    },
    [tags, deleteTagMutation, t, toast, refetch, onSuccess],
  );

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('tagsManagement.modal.title', 'Etiquetas')}
        type="default"
        width="lg"
        className="w-full sm:w-[600px] sm:max-h-[90vh]"
        fullScreenMobile={true}
      >
        {/* Crear etiqueta nueva */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-sm font-normal text-text">{t('tagsManagement.createNew', 'Crear etiqueta nueva')}</h3>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('tagsManagement.namePlaceholder', 'Ej: LL3')}
                  className="w-full sm:w-auto sm:min-w-[200px] pr-9"
                  disabled={!canManage || createTagMutation.isPending}
                  data-testid="tag-name-input"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <ColorPicker
                    value={newColor}
                    onChange={setNewColor}
                    disabled={!canManage || createTagMutation.isPending}
                    size="sm"
                  />
                </div>
              </div>
              <Button
                onClick={handleCreate}
                disabled={!canManage || createTagMutation.isPending || !newName.trim()}
                variant="outline"
                className="shrink-0 h-10"
                data-testid="create-tag-button"
              >
                {createTagMutation.isPending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 w-3.5 h-3.5 animate-spin" />
                    {t('tagsManagement.creating', 'Creando...')}
                  </>
                ) : (
                  t('tagsManagement.create', 'Crear')
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Separador */}
        <div className="border-t border-card-border mb-4" />

        {/* Lista de etiquetas */}
        <div>
          <h3 className="text-sm font-normal text-text mb-3">{t('tagsManagement.all', 'Todos')}</h3>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin text-icons" />
            </div>
          ) : tags.length === 0 ? (
            <p className="text-sm text-complementary-text py-4">
              {t('tagsManagement.empty', 'No hay etiquetas creadas. Crea una con el formulario de arriba.')}
            </p>
          ) : (
            <div className="max-h-[400px] overflow-y-auto space-y-1">
              {tags.map((tag) => (
                <div key={tag.id}>
                  {editingId === tag.id ? (
                    <div className="flex items-center gap-2 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5 text-icons" />
                      <span className="text-sm text-icons shrink-0">{t('tagsManagement.edit', 'Editar etiqueta')}</span>
                      <div className="relative flex-1 min-w-0 ml-3">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-full pr-9"
                          disabled={!canManage || updateTagMutation.isPending}
                          data-testid={`edit-tag-name-${tag.id}`}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <ColorPicker
                            value={editingColor}
                            onChange={setEditingColor}
                            disabled={!canManage || updateTagMutation.isPending}
                            size="sm"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={() => handleSaveEdit(tag.id)}
                        disabled={!canManage || updateTagMutation.isPending || !editingName.trim()}
                        variant="outline"
                        className="shrink-0 h-10"
                        data-testid={`save-tag-${tag.id}`}
                      >
                        {updateTagMutation.isPending ? (
                          <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          t('tagsManagement.save', 'Guardar')
                        )}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        disabled={updateTagMutation.isPending}
                        variant="ghost"
                        size="sm"
                        className="p-0 min-w-0"
                        data-testid={`cancel-edit-tag-${tag.id}`}
                      >
                        <FontAwesomeIcon icon={faCircleXmark} className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 px-2 py-1 rounded-lg group hover:bg-hover-press transition-colors">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 text-icons cursor-default" />
                        <span
                          className="w-fit px-3 py-0.5 rounded-full text-sm border"
                          style={
                            tag.color
                              ? {
                                  backgroundColor: tag.color,
                                  color: getTextColorForBackground(tag.color),
                                  borderColor: tag.color,
                                }
                              : {
                                  backgroundColor: 'transparent',
                                  color: 'inherit',
                                  borderColor: 'var(--wrh-card-border)',
                                }
                          }
                        >
                          {tag.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => handleStartEdit(tag)}
                          disabled={!canManage || deletingId === tag.id || deleteTagMutation.isPending}
                          className="px-2 hover:bg-hover-press rounded transition-colors disabled:opacity-50"
                          data-testid={`edit-tag-${tag.id}`}
                          aria-label={t('tagsManagement.edit', 'Editar etiqueta')}
                        >
                          <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5 text-icons" />
                        </button>
                        {deletingId === tag.id ? (
                          <span className="px-2 opacity-100" aria-hidden>
                            <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin text-icons" />
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(tag.id)}
                            disabled={!canManage || deleteTagMutation.isPending}
                            className="px-2 hover:bg-hover-press rounded transition-colors disabled:opacity-50"
                            data-testid={`delete-tag-${tag.id}`}
                            aria-label={t('tagsManagement.delete', 'Eliminar etiqueta')}
                          >
                            <FontAwesomeIcon icon={faCircleXmark} className="w-3.5 h-3.5 text-icons" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalCenter>
      <ConfirmModal ref={deleteConfirmRef} />
    </>
  );
});

ManageTagsModal.displayName = 'ManageTagsModal';
